import { useEffect, useRef, useState, useCallback } from 'react';
import { Send, MessageCircle, Mail, Inbox, FileText, List, RefreshCw, Plus, Trash2, Upload, CheckCheck, Check, Clock, AlertCircle, Users } from 'lucide-react';
import { get, post, patch, del } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import { Card, Button, Field, Input, Select, Textarea, Badge, Tabs, Loader, SectionTitle, Stat, Table, Pagination, Modal, ConfirmModal, EmptyState, SearchInput, Avatar } from '../../components/UI';
import { fmtDateTime, fmtRelative, fmtNumber } from '../../utils/format';

/**
 * Messages: broadcast to people by WhatsApp and email, manage templates, chat
 * with people who reply on WhatsApp, and see what went out.
 */
export default function Messages() {
  const { isSuperAdmin } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState('compose');
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try { setStats(await get('/messages/stats')); } catch (err) { toast.error(err.message); }
  }, [toast]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const wa = stats?.channels?.whatsapp || {}; const em = stats?.channels?.email || {};

  return (
    <>
      <SectionTitle title="Messages" subtitle="WhatsApp and email to your attendees, and their replies" />

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 13, marginBottom: 18 }}>
          <Stat label="Sent · 30 days" value={fmtNumber(stats.last30Days)} sub={`${stats.today} today`} icon={Send} tone="gold" />
          <Stat label="WhatsApp delivered" value={fmtNumber(wa.delivered + wa.read)} sub={`${wa.failed} failed · ${wa.simulated} logged only`} icon={MessageCircle} tone={stats.live?.whatsapp ? 'success' : 'warning'} />
          <Stat label="Emails sent" value={fmtNumber(em.sent)} sub={`${em.failed} failed · ${em.simulated} logged only`} icon={Mail} tone={stats.live?.email ? 'info' : 'warning'} />
          <Stat label="Replies received" value={fmtNumber(wa.received)} sub={stats.unreadConversations ? `${stats.unreadConversations} unread` : 'All read'} icon={Inbox} tone={stats.unreadConversations ? 'gold' : 'neutral'} />
        </div>
      )}
      {stats && (!stats.live?.whatsapp || !stats.live?.email) && (
        <p style={{ fontSize: 12.5, color: 'var(--warn)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
          <AlertCircle size={14} />
          {!stats.live?.whatsapp && !stats.live?.email ? 'WhatsApp and email are not connected — messages are logged but not delivered.'
            : !stats.live?.whatsapp ? 'WhatsApp is not connected — WhatsApp messages are logged but not delivered.'
            : 'Email is not connected — emails are logged but not delivered.'}
          {isSuperAdmin ? ' Connect them in Settings.' : ' Ask the super admin to connect them in Settings.'}
        </p>
      )}

      <Tabs active={tab} onChange={setTab} tabs={[
        { key: 'compose', label: 'Send a message', icon: Send },
        { key: 'inbox', label: `Inbox${stats?.unreadConversations ? ` (${stats.unreadConversations})` : ''}`, icon: Inbox },
        { key: 'templates', label: 'Templates', icon: FileText },
        { key: 'log', label: 'Sent log', icon: List },
      ]} />

      {tab === 'compose' && <Compose onSent={loadStats} />}
      {tab === 'inbox' && <InboxView onChange={loadStats} />}
      {tab === 'templates' && <Templates canEdit={isSuperAdmin} />}
      {tab === 'log' && <Log />}
    </>
  );
}

/* ── compose ─────────────────────────────────────────────────────────── */

function Compose({ onSent }) {
  const { eventId, events } = useEvents();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [fields, setFields] = useState([]);
  const [aud, setAud] = useState({ mode: 'event', filters: {}, users: [] });
  const [people, setPeople] = useState(null);
  const [search, setSearch] = useState('');
  const [channels, setChannels] = useState({ whatsapp: true, email: true });
  const [wa, setWa] = useState({ template: '', text: '' });
  const [em, setEm] = useState({ template: '', subject: '', body: '' });
  const [sending, setSending] = useState(null);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    get('/messages/templates').then((d) => { setTemplates(d.templates || []); setFields(d.fields || []); }).catch(() => {});
  }, []);

  const audienceBody = useCallback(() => ({
    event: eventId || undefined,
    users: aud.mode === 'people' ? aud.users : undefined,
    filters: aud.mode === 'group' ? aud.filters : undefined,
  }), [eventId, aud]);

  useEffect(() => {
    if (!eventId) { setPeople(null); return; }
    let alive = true;
    post('/messages/audience', audienceBody()).then((r) => alive && setPeople(r.data)).catch((err) => toast.error(err.message));
    return () => { alive = false; };
  }, [audienceBody, eventId, toast]);

  const waTemplates = templates.filter((t) => t.channel === 'whatsapp' && t.isActive !== false);
  const emTemplates = templates.filter((t) => t.channel === 'email' && t.isActive !== false);
  const waTpl = waTemplates.find((t) => t._id === wa.template);

  const recipients = aud.mode === 'people' ? aud.users.length : (people?.count || 0);

  const insert = (setter, obj, key, token) => setter({ ...obj, [key]: `${obj[key] || ''}{{${token}}}` });

  const send = async () => {
    setConfirm(false);
    const ids = aud.mode === 'people' ? aud.users : (people?.people || []).map((p) => p._id);
    if (!ids.length) return toast.error('Nobody to send to');
    if (people && aud.mode !== 'people' && people.count > people.people.length) {
      // audience preview is capped; ask the server for everyone
      const full = await post('/messages/audience', { ...audienceBody() });
      if (full.data.count > full.data.people.length) return toast.error('This audience is too large for one send — narrow it with a group filter');
    }
    const payload = {
      event: eventId || undefined,
      ...(channels.whatsapp ? { whatsapp: wa.template ? { template: wa.template } : { text: wa.text } } : {}),
      ...(channels.email ? { email: em.template ? { template: em.template } : { subject: em.subject, body: em.body } } : {}),
    };
    if (!payload.whatsapp && !payload.email) return toast.error('Pick WhatsApp, email or both');
    if (payload.whatsapp && !wa.template && !wa.text.trim()) return toast.error('Pick a WhatsApp template or write the message');
    if (payload.email && !em.template && !(em.subject.trim() && em.body.trim())) return toast.error('Pick an email template or write a subject and message');

    const totals = { whatsapp: { sent: 0, failed: 0, skipped: 0 }, email: { sent: 0, failed: 0, skipped: 0 }, failures: [] };
    setSending({ done: 0, total: ids.length });
    for (let i = 0; i < ids.length; i += 50) {
      try {
        const res = await post('/messages/send', { ...payload, users: ids.slice(i, i + 50) });
        for (const ch of ['whatsapp', 'email']) for (const k of ['sent', 'failed', 'skipped']) totals[ch][k] += res.data[ch][k];
        totals.failures.push(...res.data.failures);
      } catch (err) { totals.failures.push({ name: `${Math.min(50, ids.length - i)} people`, reason: err.message }); }
      setSending({ done: Math.min(i + 50, ids.length), total: ids.length, totals });
    }
    setSending({ done: ids.length, total: ids.length, totals, finished: true });
    onSent();
  };

  if (!events.length) return <EmptyState message="No events yet" hint="Messages are sent to the people on an event." icon={Users} />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1.4fr)', gap: 16, alignItems: 'start' }} className="gl-compose">
      <Card padding={20}>
        <p style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 12 }}>Who</p>
        <EventPicker style={{ marginBottom: 14 }} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {[['event', 'Everyone on the event'], ['group', 'A group'], ['people', 'Selected people']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setAud({ ...aud, mode: k })}
              style={{ padding: '7px 12px', fontSize: 12.5, borderRadius: 99, border: `1px solid ${aud.mode === k ? 'var(--gold-500)' : 'var(--border)'}`, background: aud.mode === k ? 'rgba(239,212,138,0.1)' : 'transparent', color: aud.mode === k ? 'var(--gold-300)' : 'var(--text-mid)' }}>
              {l}
            </button>
          ))}
        </div>

        {aud.mode === 'group' && people && (
          <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
            {[['department', 'Department'], ['designation', 'Designation / role'], ['city', 'City'], ['state', 'State']].map(([k, l]) => (
              people.groups?.[k]?.length ? (
                <Field key={k} label={l}>
                  <Select value={aud.filters[k] || ''} onChange={(e) => setAud({ ...aud, filters: { ...aud.filters, [k]: e.target.value || undefined } })}>
                    <option value="">Any</option>
                    {people.groups[k].map((v) => <option key={v} value={v}>{v}</option>)}
                  </Select>
                </Field>
              ) : null
            ))}
            {people.roomGroups?.length > 0 && (
              <Field label="Rooming group">
                <Select value={aud.filters.roomGroup || ''} onChange={(e) => setAud({ ...aud, filters: { ...aud.filters, roomGroup: e.target.value || undefined } })}>
                  <option value="">Any</option>
                  {people.roomGroups.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
              </Field>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-mid)', cursor: 'pointer' }}>
              <input type="checkbox" checked={Boolean(aud.filters.isVip)} onChange={(e) => setAud({ ...aud, filters: { ...aud.filters, isVip: e.target.checked || undefined } })} style={{ accentColor: 'var(--gold-400)' }} />
              VIP guests only
            </label>
          </div>
        )}

        {aud.mode === 'people' && people && (
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a person…" style={{ marginBottom: 8 }} />
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
              {people.people.filter((p) => !search || `${p.name} ${p.mobile} ${p.email}`.toLowerCase().includes(search.toLowerCase())).map((p) => {
                const on = aud.users.includes(p._id);
                return (
                  <label key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: on ? 'rgba(239,212,138,0.06)' : 'transparent' }}>
                    <input type="checkbox" checked={on} onChange={(e) => setAud({ ...aud, users: e.target.checked ? [...aud.users, p._id] : aud.users.filter((id) => id !== p._id) })} style={{ accentColor: 'var(--gold-400)' }} />
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 13 }}>{p.name}</p>
                      <p style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{[p.mobile, p.email].filter(Boolean).join(' · ') || 'no contact details'}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Button variant="subtle" size="sm" onClick={() => setAud({ ...aud, users: people.people.map((p) => p._id) })}>Select all</Button>
              <Button variant="subtle" size="sm" onClick={() => setAud({ ...aud, users: [] })}>Clear</Button>
            </div>
          </>
        )}

        <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r)', fontSize: 13 }}>
          {!eventId ? 'Pick an event' : people ? (
            <>
              <strong style={{ color: 'var(--gold-300)' }}>{recipients}</strong> {recipients === 1 ? 'person' : 'people'}
              {aud.mode !== 'people' && <span style={{ color: 'var(--text-lo)' }}> · {people.withMobile} with a mobile · {people.withEmail} with an email</span>}
            </>
          ) : 'Counting…'}
        </div>
      </Card>

      <Card padding={20}>
        <p style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 12 }}>What</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[['whatsapp', 'WhatsApp', MessageCircle], ['email', 'Email', Mail]].map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setChannels({ ...channels, [k]: !channels[k] })}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 13px', fontSize: 12.5, borderRadius: 99, border: `1px solid ${channels[k] ? 'var(--gold-500)' : 'var(--border)'}`, background: channels[k] ? 'rgba(239,212,138,0.1)' : 'transparent', color: channels[k] ? 'var(--gold-300)' : 'var(--text-mid)' }}>
              <Icon size={13} /> {l}
            </button>
          ))}
        </div>

        {channels.whatsapp && (
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
            <Field label="WhatsApp" hint={waTemplates.some((t) => t.whatsapp?.status === 'approved') ? undefined : 'No approved template yet — a custom message only reaches people who wrote to you in the last 24 hours'}>
              <Select value={wa.template} onChange={(e) => setWa({ ...wa, template: e.target.value })}>
                <option value="">Custom message</option>
                {waTemplates.map((t) => <option key={t._id} value={t._id} disabled={t.whatsapp?.status !== 'approved'}>{t.name}{t.whatsapp?.status !== 'approved' ? ` (${t.whatsapp?.status})` : ''}</option>)}
              </Select>
            </Field>
            {waTpl ? (
              <p style={{ fontSize: 12.5, color: 'var(--text-mid)', whiteSpace: 'pre-wrap', padding: '10px 12px', background: 'var(--ink-900)', borderRadius: 'var(--r)', border: '1px solid var(--border)' }}>{waTpl.body}</p>
            ) : (
              <>
                <Textarea rows={4} value={wa.text} onChange={(e) => setWa({ ...wa, text: e.target.value })} placeholder="Hi {{firstName}}, the coach to the venue leaves at 8:00 from the lobby." />
                <Placeholders fields={fields} onPick={(k) => insert(setWa, wa, 'text', k)} />
              </>
            )}
          </div>
        )}

        {channels.email && (
          <div style={{ marginBottom: 8 }}>
            <Field label="Email">
              <Select value={em.template} onChange={(e) => setEm({ ...em, template: e.target.value })}>
                <option value="">Custom email</option>
                {emTemplates.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
              </Select>
            </Field>
            {em.template ? (
              <p style={{ fontSize: 12.5, color: 'var(--text-mid)', whiteSpace: 'pre-wrap', padding: '10px 12px', background: 'var(--ink-900)', borderRadius: 'var(--r)', border: '1px solid var(--border)' }}>
                <strong>{emTemplates.find((t) => t._id === em.template)?.subject}</strong>{'\n'}{emTemplates.find((t) => t._id === em.template)?.body}
              </p>
            ) : (
              <>
                <Field label="Subject"><Input value={em.subject} onChange={(e) => setEm({ ...em, subject: e.target.value })} placeholder="Tomorrow's schedule for {{eventTitle}}" /></Field>
                <Textarea rows={6} value={em.body} onChange={(e) => setEm({ ...em, body: e.target.value })} placeholder="Dear {{name}}, …" />
                <Placeholders fields={fields} onPick={(k) => insert(setEm, em, 'body', k)} />
              </>
            )}
          </div>
        )}

        {sending && (
          <div style={{ margin: '12px 0', padding: '10px 12px', background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r)', fontSize: 12.5 }}>
            <div style={{ height: 6, borderRadius: 99, background: 'var(--ink-800)', overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: `${Math.round((sending.done / sending.total) * 100)}%`, background: 'var(--gold-400)', transition: 'width .3s' }} />
            </div>
            {sending.totals && (
              <p style={{ color: 'var(--text-mid)' }}>
                {channels.whatsapp && <>WhatsApp: {sending.totals.whatsapp.sent} sent{sending.totals.whatsapp.failed ? `, ${sending.totals.whatsapp.failed} failed` : ''}{sending.totals.whatsapp.skipped ? `, ${sending.totals.whatsapp.skipped} without a number` : ''}. </>}
                {channels.email && <>Email: {sending.totals.email.sent} sent{sending.totals.email.failed ? `, ${sending.totals.email.failed} failed` : ''}{sending.totals.email.skipped ? `, ${sending.totals.email.skipped} without an address` : ''}.</>}
              </p>
            )}
            {sending.totals?.failures?.slice(0, 8).map((f, i) => <p key={i} style={{ color: 'var(--text-lo)', marginTop: 3 }}><span style={{ color: 'var(--warn)' }}>{f.name}</span> ({f.channel}) — {f.reason}</p>)}
          </div>
        )}

        <Button icon={Send} onClick={() => setConfirm(true)} disabled={!eventId || !recipients || (!channels.whatsapp && !channels.email) || (sending && !sending.finished)}>
          Send to {recipients} {recipients === 1 ? 'person' : 'people'}
        </Button>
      </Card>

      <ConfirmModal open={confirm} onClose={() => setConfirm(false)} onConfirm={send} title="Send this message?" confirmLabel="Send now"
        message={`${recipients} ${recipients === 1 ? 'person' : 'people'} on ${events.find((e) => e._id === eventId)?.title || 'this event'} will get it by ${[channels.whatsapp && 'WhatsApp', channels.email && 'email'].filter(Boolean).join(' and ')}.`} />
      <style>{'@media (max-width: 860px) { .gl-compose { grid-template-columns: 1fr !important; } }'}</style>
    </div>
  );
}

function Placeholders({ fields, onPick }) {
  if (!fields.length) return null;
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
      {fields.map((f) => (
        <button key={f.key} type="button" onClick={() => onPick(f.key)} title={`Inserts {{${f.key}}} — e.g. ${f.sample}`}
          style={{ fontSize: 11, padding: '3px 8px', borderRadius: 99, border: '1px solid var(--border)', color: 'var(--text-mid)', background: 'transparent' }}>
          {f.label}
        </button>
      ))}
    </div>
  );
}

/* ── templates ───────────────────────────────────────────────────────── */

const BLANK_TPL = { name: '', channel: 'whatsapp', purpose: 'custom', subject: '', body: '', category: 'UTILITY' };
const STATUS_TONE = { approved: 'success', pending: 'warning', rejected: 'danger', draft: 'neutral', paused: 'warning', disabled: 'danger' };

function Templates({ canEdit }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    try { setData(await get('/messages/templates')); } catch (err) { toast.error(err.message); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const res = editing._id ? await patch(`/messages/templates/${editing._id}`, editing) : await post('/messages/templates', editing);
      toast.success(res.message); setEditing(null); load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  const submit = async (t) => {
    setBusy(true);
    try { const res = await post(`/messages/templates/${t._id}/submit`, {}); toast.success(res.message); load(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  const sync = async () => {
    setBusy(true);
    try { const res = await post('/messages/templates/sync', {}); toast.success(res.message); setData(res.data); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { const res = await del(`/messages/templates/${removing._id}`); toast.success(res.message); setRemoving(null); load(); }
    catch (err) { toast.error(err.message); }
  };
  const showPreview = async (t) => {
    try { const r = await post('/messages/templates/preview', { subject: t.subject, body: t.body }); setPreview({ ...t, ...r.data }); }
    catch (err) { toast.error(err.message); }
  };

  if (!data) return <Loader label="Loading templates" />;
  const rows = data.templates || [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <p style={{ fontSize: 12.5, color: 'var(--text-lo)', maxWidth: 640, lineHeight: 1.6 }}>
          Templates fill in each person's details from placeholders like <code>{'{{firstName}}'}</code>. A template marked
          <strong> Login details</strong> is used automatically when passwords are sent. WhatsApp templates must be approved by Meta before they can start a conversation.
        </p>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" icon={RefreshCw} onClick={sync} loading={busy}>Check approvals</Button>
            <Button icon={Plus} onClick={() => setEditing({ ...BLANK_TPL })}>New template</Button>
          </div>
        )}
      </div>

      {!rows.length ? <EmptyState message="No templates yet" hint="Create one for login details, reminders or a welcome note." icon={FileText} /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {rows.map((t) => (
            <Card key={t._id} padding={16}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                    {t.channel === 'whatsapp' ? <MessageCircle size={14} color="#25D366" /> : <Mail size={14} color="var(--gold-500)" />}{t.name}
                  </p>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                    {t.purpose !== 'custom' && <Badge tone="gold">{{ credentials: 'Login details', reminder: 'Reminder', welcome: 'Welcome' }[t.purpose]}</Badge>}
                    {t.channel === 'whatsapp' && <Badge tone={STATUS_TONE[t.whatsapp?.status] || 'neutral'}>{t.whatsapp?.status === 'pending' ? 'Awaiting approval' : t.whatsapp?.status || 'draft'}</Badge>}
                    {t.isActive === false && <Badge tone="neutral">Inactive</Badge>}
                  </div>
                </div>
              </div>
              {t.subject && <p style={{ fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>{t.subject}</p>}
              <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 6, whiteSpace: 'pre-wrap', maxHeight: 96, overflow: 'hidden', lineHeight: 1.55 }}>{t.body}</p>
              {t.whatsapp?.rejectedReason && <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>Meta: {t.whatsapp.rejectedReason}</p>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                <Button variant="subtle" size="sm" onClick={() => showPreview(t)}>Preview</Button>
                {canEdit && <Button variant="subtle" size="sm" onClick={() => setEditing({ ...t, category: t.whatsapp?.category || 'UTILITY' })}>Edit</Button>}
                {canEdit && t.channel === 'whatsapp' && ['draft', 'rejected'].includes(t.whatsapp?.status) && (
                  <Button variant="outline" size="sm" icon={Upload} onClick={() => submit(t)} loading={busy}>Submit for approval</Button>
                )}
                {canEdit && <Button variant="subtle" size="sm" icon={Trash2} onClick={() => setRemoving(t)} />}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={Boolean(editing)} onClose={() => setEditing(null)} title={editing?._id ? 'Edit template' : 'New template'} width={640}
        footer={<><Button variant="subtle" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} loading={busy}>Save</Button></>}>
        {editing && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <Field label="Name" required><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Login details" /></Field>
              <Field label="Channel">
                <Select value={editing.channel} disabled={Boolean(editing._id)} onChange={(e) => setEditing({ ...editing, channel: e.target.value })}>
                  <option value="whatsapp">WhatsApp</option><option value="email">Email</option>
                </Select>
              </Field>
              <Field label="Used for">
                <Select value={editing.purpose} onChange={(e) => setEditing({ ...editing, purpose: e.target.value })}>
                  <option value="custom">Sending by hand</option>
                  <option value="credentials">Login details (sent automatically)</option>
                  <option value="reminder">Reminders</option>
                  <option value="welcome">Welcome</option>
                </Select>
              </Field>
              {editing.channel === 'whatsapp' && (
                <Field label="Meta category" hint="Utility for service messages, marketing for promotions">
                  <Select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                    <option value="UTILITY">Utility</option><option value="MARKETING">Marketing</option>
                  </Select>
                </Field>
              )}
            </div>
            {editing.channel === 'email' && <Field label="Subject" required><Input value={editing.subject || ''} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} placeholder="Your login for {{eventTitle}}" /></Field>}
            <Field label="Message" required hint={editing.channel === 'whatsapp' ? 'Plain text, up to 1024 characters. Editing an approved template creates a new one that needs approval again.' : 'Plain text; links become clickable'}>
              <Textarea rows={7} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                placeholder={"Hi {{firstName}}, you're invited to {{eventTitle}}.\nSign in at {{link}} with login {{loginId}} and password {{password}}."} />
            </Field>
            <Placeholders fields={data.fields || []} onPick={(k) => setEditing({ ...editing, body: `${editing.body || ''}{{${k}}}` })} />
            {editing.purpose === 'credentials' && !/\{\{\s*(password|loginId)\s*\}\}/.test(editing.body || '') && (
              <p style={{ fontSize: 12, color: 'var(--warn)', marginTop: 10 }}>A login-details template should include {'{{loginId}}'} and {'{{password}}'}.</p>
            )}
          </>
        )}
      </Modal>

      <Modal open={Boolean(preview)} onClose={() => setPreview(null)} title="Preview with sample details" subtitle={preview?.name} width={520}>
        {preview && (
          <div style={{ padding: 14, background: preview.channel === 'whatsapp' ? '#0b141a' : 'var(--ink-900)', borderRadius: 'var(--r)', border: '1px solid var(--border)' }}>
            {preview.subject && <p style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 8 }}>{preview.subject}</p>}
            <p style={{ fontSize: 13.5, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{preview.body}</p>
          </div>
        )}
      </Modal>

      <ConfirmModal open={Boolean(removing)} onClose={() => setRemoving(null)} onConfirm={remove} danger title="Delete template" confirmLabel="Delete"
        message={`"${removing?.name}" will be removed${removing?.whatsapp?.metaId ? ' here and at Meta' : ''}.`} />
    </>
  );
}

/* ── inbox ───────────────────────────────────────────────────────────── */

function InboxView({ onChange }) {
  const toast = useToast();
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottom = useRef(null);

  const loadThreads = useCallback(async () => {
    try { setThreads(await get('/messages/conversations')); } catch (err) { toast.error(err.message); }
  }, [toast]);
  const loadThread = useCallback(async (key) => {
    try { setThread(await get(`/messages/conversations/${key}`)); onChange(); } catch (err) { toast.error(err.message); }
  }, [toast, onChange]);

  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 20000); return () => clearInterval(t); }, [loadThreads]);
  useEffect(() => { if (active) { loadThread(active); const t = setInterval(() => loadThread(active), 10000); return () => clearInterval(t); } }, [active, loadThread]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread?.messages?.length]);

  const reply = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await post(`/messages/conversations/${active}/reply`, { text }); setText(''); loadThread(active); loadThreads(); }
    catch (err) { toast.error(err.message); }
    finally { setSending(false); }
  };

  if (!threads) return <Loader label="Loading conversations" />;
  if (!threads.length) return <EmptyState message="No WhatsApp conversations yet" hint="Replies from attendees appear here once the webhook is set up in Settings." icon={Inbox} />;

  const Tick = ({ status }) => status === 'read' ? <CheckCheck size={12} color="#53bdeb" /> : status === 'delivered' ? <CheckCheck size={12} /> : status === 'sent' ? <Check size={12} /> : status === 'failed' ? <AlertCircle size={12} color="var(--danger)" /> : <Clock size={12} />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 320px) 1fr', gap: 14, minHeight: 480 }} className="gl-inbox">
      <Card padding={0} style={{ overflow: 'hidden' }}>
        <div style={{ maxHeight: 560, overflowY: 'auto' }}>
          {threads.map((t) => (
            <button key={t.key} type="button" onClick={() => setActive(t.key)}
              style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '11px 13px', borderBottom: '1px solid var(--border)', background: active === t.key ? 'rgba(239,212,138,0.07)' : 'transparent' }}>
              <Avatar name={t.user?.name || t.mobile} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <p style={{ fontSize: 13.5, fontWeight: t.unread ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.user?.name || `+${t.key}`}</p>
                  <span style={{ fontSize: 11, color: 'var(--text-lo)', flexShrink: 0 }}>{fmtRelative(t.last.at)}</span>
                </div>
                <p style={{ fontSize: 12, color: t.unread ? 'var(--text-hi)' : 'var(--text-lo)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
                  {t.last.direction === 'out' ? 'You: ' : ''}{t.last.body}
                </p>
              </div>
              {t.unread > 0 && <span style={{ alignSelf: 'center', minWidth: 20, height: 20, borderRadius: 99, background: 'var(--gold-400)', color: '#000', fontSize: 11, fontWeight: 700, display: 'grid', placeItems: 'center', padding: '0 6px' }}>{t.unread}</span>}
            </button>
          ))}
        </div>
      </Card>

      <Card padding={0} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!active ? <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-lo)', fontSize: 13, padding: 30 }}>Pick a conversation</div> : !thread ? <Loader label="Opening" /> : (
          <>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar name={thread.user?.name || thread.mobile} size={34} />
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 600 }}>{thread.user?.name || 'Unknown number'}</p>
                <p style={{ fontSize: 12, color: 'var(--text-lo)' }}>+{thread.key}{thread.user?.corporate?.name ? ` · ${thread.user.corporate.name}` : ''}{thread.user?.events?.length ? ` · ${thread.user.events.map((e) => e.title).join(', ')}` : ''}</p>
              </div>
              <span style={{ marginLeft: 'auto' }}>
                {thread.windowOpen ? <Badge tone="success">Reply window open</Badge> : <Badge tone="warning" style={{ whiteSpace: 'normal' }}>Free-text replies only work for 24h after they write</Badge>}
              </span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 440, background: '#0b141a' }}>
              {thread.messages.map((m) => (
                <div key={m._id} style={{ alignSelf: m.direction === 'out' ? 'flex-end' : 'flex-start', maxWidth: '78%', padding: '8px 11px', borderRadius: 10, background: m.direction === 'out' ? '#005c4b' : '#202c33', color: '#e9edef', fontSize: 13.5, lineHeight: 1.5 }}>
                  {m.templateName && m.direction === 'out' && <p style={{ fontSize: 10.5, color: '#8696a0', marginBottom: 3 }}>Template · {m.templateName}</p>}
                  <p style={{ whiteSpace: 'pre-wrap' }}>{m.body}</p>
                  <p style={{ fontSize: 10.5, color: '#8696a0', marginTop: 4, display: 'flex', gap: 5, alignItems: 'center', justifyContent: 'flex-end' }}>
                    {m.sentBy?.name && m.direction === 'out' ? `${m.sentBy.name} · ` : ''}{fmtDateTime(m.createdAt)} {m.direction === 'out' && <Tick status={m.status} />}
                  </p>
                  {m.error && <p style={{ fontSize: 11, color: '#f5a3a3', marginTop: 3 }}>{m.error}</p>}
                </div>
              ))}
              <div ref={bottom} />
            </div>
            <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a reply…" onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); reply(); } }} style={{ flex: 1 }} />
              <Button icon={Send} onClick={reply} loading={sending} disabled={!text.trim()}>Send</Button>
            </div>
          </>
        )}
      </Card>
      <style>{'@media (max-width: 760px) { .gl-inbox { grid-template-columns: 1fr !important; } }'}</style>
    </div>
  );
}

/* ── log ─────────────────────────────────────────────────────────────── */

function Log() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState({ channel: '', status: '', search: '', page: 1 });

  useEffect(() => {
    let alive = true;
    get('/messages', { ...q, direction: 'out', limit: 25 }, true)
      .then((r) => { if (alive) { setRows(r.data); setMeta(r.meta); } }).catch((err) => toast.error(err.message));
    return () => { alive = false; };
  }, [q, toast]);

  const tone = { sent: 'info', delivered: 'success', read: 'success', failed: 'danger', simulated: 'neutral', queued: 'warning' };
  const columns = [
    { key: 'when', title: 'When', render: (r) => <span style={{ fontSize: 12.5 }}>{fmtDateTime(r.createdAt)}</span> },
    { key: 'channel', title: 'Channel', render: (r) => r.channel === 'whatsapp' ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MessageCircle size={13} color="#25D366" /> WhatsApp</span> : <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={13} color="var(--gold-500)" /> Email</span> },
    { key: 'to', title: 'To', render: (r) => <div><p style={{ fontSize: 13 }}>{r.user?.name || '—'}</p><p style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{r.to}</p></div> },
    { key: 'body', title: 'Message', render: (r) => <div style={{ maxWidth: 360 }}>{r.subject && <p style={{ fontSize: 12.5, fontWeight: 600 }}>{r.subject}</p>}<p style={{ fontSize: 12.5, color: 'var(--text-mid)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.body}</p></div> },
    { key: 'purpose', title: 'Type', render: (r) => <Badge tone="neutral">{{ credentials: 'Login details', broadcast: 'Broadcast', notification: 'Notification', reply: 'Reply', custom: 'Custom' }[r.purpose] || r.purpose}</Badge> },
    { key: 'status', title: 'Status', render: (r) => <div><Badge tone={tone[r.status] || 'neutral'}>{r.status === 'simulated' ? 'logged only' : r.status}</Badge>{r.error && <p style={{ fontSize: 11, color: 'var(--danger)', marginTop: 3, maxWidth: 200 }}>{r.error}</p>}</div> },
    { key: 'by', title: 'Sent by', render: (r) => <span style={{ fontSize: 12.5, color: 'var(--text-mid)' }}>{r.sentBy?.name || 'System'}</span> },
  ];

  return (
    <>
      <Card padding={14} style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <SearchInput value={q.search} onChange={(e) => setQ({ ...q, search: e.target.value, page: 1 })} placeholder="Search number, address or text…" style={{ flex: 1, minWidth: 200 }} />
          <Select value={q.channel} onChange={(e) => setQ({ ...q, channel: e.target.value, page: 1 })} style={{ width: 150 }}>
            <option value="">All channels</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option>
          </Select>
          <Select value={q.status} onChange={(e) => setQ({ ...q, status: e.target.value, page: 1 })} style={{ width: 150 }}>
            <option value="">Any status</option><option value="sent">Sent</option><option value="delivered">Delivered</option><option value="read">Read</option><option value="failed">Failed</option><option value="simulated">Logged only</option>
          </Select>
        </div>
      </Card>
      <Table columns={columns} rows={rows} loading={!rows} empty="Nothing sent yet" />
      <Pagination meta={meta} onChange={(page) => setQ({ ...q, page })} />
    </>
  );
}
